import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { iconByName } from "@/lib/icons";
import { CornerDownLeft, LayoutDashboard, Loader2, Search, type LucideIcon } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { MENU_SECTIONS, isMenuSubGroup, type MenuPath } from "@/lib/menu-paths";
import { useMenuAccess } from "@/hooks/shared/use-menu-access";
import { useDebouncedValue } from "@/hooks/shared/use-debounced-value";
import { useGlobalSearch } from "@/hooks/shared/use-global-search";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getIcon(name?: string): LucideIcon {
  if (!name) return LayoutDashboard;
  return iconByName(name) ?? LayoutDashboard;
}

/** Itens de menu (planos) a que o utilizador tem acesso. */
function useAccessibleMenuItems(): MenuPath[] {
  const { hasMenuAccess } = useMenuAccess();
  return useMemo(() => {
    const items: MenuPath[] = [];
    for (const section of MENU_SECTIONS) {
      for (const entry of section.items) {
        const paths = isMenuSubGroup(entry) ? entry.items : [entry];
        for (const item of paths) {
          if (hasMenuAccess(item.path)) items.push(item);
        }
      }
    }
    return items;
  }, [hasMenuAccess]);
}

export function CommandPalette({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 250);

  const menuItems = useAccessibleMenuItems();
  const { data: groups = [], isFetching } = useGlobalSearch(debounced);

  // Navegação: filtra as páginas acessíveis pelo termo (local, instantâneo).
  const q = query.trim().toLowerCase();
  const navMatches = useMemo(() => {
    if (!q) return menuItems.slice(0, 8); // atalhos por defeito quando vazio
    return menuItems.filter((m) => m.label.toLowerCase().includes(q)).slice(0, 8);
  }, [menuItems, q]);

  const go = (url: string) => {
    onOpenChange(false);
    setQuery("");
    navigate(url);
  };

  const hasEntityResults = groups.some((g) => g.items.length > 0);
  const showEmpty = q.length >= 2 && !isFetching && navMatches.length === 0 && !hasEntityResults;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Pesquisa global"
      description="Pesquisar páginas e conteúdo em toda a plataforma"
      shouldFilter={false}
      // O × cai em cima do campo de pesquisa. Fecha-se com Esc, e o rodapé di-lo.
      showCloseButton={false}
      className={cn(
        "top-[15%] max-w-[min(640px,calc(100vw-2rem))] translate-y-0 gap-0 rounded-2xl",
        "border-border/60 p-0 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.25)]"
      )}
      commandClassName={cn(
        // Grupos: rótulo em maiúsculas espaçadas, como no resto da app.
        "**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:pb-1.5 **:[[cmdk-group-heading]]:pt-3",
        "**:[[cmdk-group-heading]]:text-[10px] **:[[cmdk-group-heading]]:font-medium",
        "**:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-[0.18em]",
        "**:[[cmdk-item]]:rounded-lg **:[[cmdk-item]]:px-3 **:[[cmdk-item]]:py-2.5",
        "[&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4",
        // A barra de pesquisa: altura e respiro lateral (o preset deixa-a
        // apertada contra o bordo), e a lupa alinhada com o texto.
        "[&_[data-slot=command-input-wrapper]_svg]:h-[18px] [&_[data-slot=command-input-wrapper]_svg]:w-[18px]",
        "[&_[data-slot=command-input-wrapper]_svg]:text-muted-foreground/50 [&_[data-slot=command-input-wrapper]_svg]:opacity-100",
        // O preset impõe h-12 ao input; sem isto ele não preenche o wrapper e
        // o texto fica desalinhado da lupa.
        "**:[[cmdk-input]]:h-full"
      )}
    >
      <CommandInput
        placeholder="Pesquisar páginas, clientes, encomendas…"
        value={query}
        onValueChange={setQuery}
        className="h-full border-0 p-0 text-[15px] outline-hidden ring-0 placeholder:text-muted-foreground/50 focus:ring-0 focus-visible:outline-hidden focus-visible:ring-0"
      />

      <CommandList className="max-h-[min(440px,60vh)] px-2 pb-2">
        {/* Nada escrito e nada para sugerir: diz-se o que isto faz. */}
        {!q && navMatches.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-14 text-center">
            <Search className="h-5 w-5 text-muted-foreground/30" strokeWidth={1.5} />
            <p className="text-[13px] text-muted-foreground">Pesquisa em toda a plataforma</p>
            <p className="text-xs text-muted-foreground/60">
              Páginas, clientes, encomendas e muito mais.
            </p>
          </div>
        )}

        {showEmpty && (
          <CommandEmpty className="py-14">
            <div className="flex flex-col items-center gap-2 text-center">
              <Search className="h-5 w-5 text-muted-foreground/30" strokeWidth={1.5} />
              <p className="text-[13px] text-muted-foreground">
                Nada encontrado para <span className="text-foreground">“{query}”</span>
              </p>
              <p className="text-xs text-muted-foreground/60">Tenta outra palavra.</p>
            </div>
          </CommandEmpty>
        )}

        {navMatches.length > 0 && (
          <CommandGroup heading={q ? "Páginas" : "Ir para"}>
            {navMatches.map((item) => {
              const Icon = getIcon(item.icon);
              return (
                <CommandItem
                  key={item.path}
                  value={`nav:${item.path}`}
                  onSelect={() => go(item.path)}
                  // O preset do shadcn esbate o texto do item seleccionado —
                  // sobre o fundo cinzento fica ilegível. Força-se o contraste.
                  className={cn(
                    "group gap-3 text-[13px] text-foreground",
                    "data-[selected=true]:bg-foreground/5 data-[selected=true]:text-foreground"
                  )}
                >
                  <Icon className="shrink-0 text-muted-foreground/70" strokeWidth={1.75} />
                  <span className="truncate">{item.label}</span>
                  <Enter />
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {groups.map((group) => (
          <CommandGroup key={group.type} heading={group.label}>
            {group.items.map((hit) => {
              const Icon = getIcon(hit.icon);
              return (
                <CommandItem
                  key={`${group.type}:${hit.id}`}
                  value={`${group.type}:${hit.id}`}
                  onSelect={() => go(hit.url)}
                  // O preset do shadcn esbate o texto do item seleccionado —
                  // sobre o fundo cinzento fica ilegível. Força-se o contraste.
                  className={cn(
                    "group gap-3 text-[13px] text-foreground",
                    "data-[selected=true]:bg-foreground/5 data-[selected=true]:text-foreground"
                  )}
                >
                  <Icon className="shrink-0 text-muted-foreground/70" strokeWidth={1.75} />
                  <span className="truncate">{hit.label}</span>
                  {hit.sublabel && (
                    <span className="ml-auto truncate pl-3 text-xs text-muted-foreground/70">
                      {hit.sublabel}
                    </span>
                  )}
                  <Enter className={hit.sublabel ? "ml-2" : undefined} />
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}

        {isFetching && q.length >= 2 && (
          <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />A pesquisar…
          </div>
        )}
      </CommandList>

      {/* As regras do jogo, à vista — quem descobre o ⌘K merece saber o resto. */}
      <footer className="flex items-center gap-4 border-t border-border/60 px-4 py-2.5 text-[11px] text-muted-foreground">
        <Hint keys={["↑", "↓"]}>navegar</Hint>
        <Hint keys={["↵"]}>abrir</Hint>
        <Hint keys={["esc"]}>fechar</Hint>
      </footer>
    </CommandDialog>
  );
}

/**
 * O ↵ que aparece na linha seleccionada — diz o que a tecla Enter vai fazer.
 *
 * `auto` empurra-o para a direita quando é o último elemento; quando já há um
 * sublabel encostado à direita, passa-se `ml-2` para ele ficar a seguir e não
 * disputarem o mesmo `ml-auto`.
 */
const Enter = ({ className = "ml-auto" }: { className?: string }) => (
  <CornerDownLeft
    className={cn(
      "shrink-0 text-muted-foreground/40 opacity-0 transition-opacity",
      "group-data-[selected=true]:opacity-100",
      className
    )}
    strokeWidth={1.75}
  />
);

const Hint = ({ keys, children }: { keys: string[]; children: React.ReactNode }) => (
  <span className="flex items-center gap-1.5">
    <span className="flex gap-1">
      {keys.map((k) => (
        <kbd
          key={k}
          className="grid h-[18px] min-w-[18px] place-items-center rounded border border-border/60 bg-muted/50 px-1 font-sans text-[10px] font-medium leading-none"
        >
          {k}
        </kbd>
      ))}
    </span>
    {children}
  </span>
);
